/**
 * Address formatting for list views.
 *
 * Imported contacts very often have only some of the four address fields
 * filled in. Joining them with a fixed template left the separators behind for
 * the missing ones, so a contact with only a state showed as ": , , Arizona,".
 * Every helper here drops empty parts before joining.
 */

const clean = (value) => {
  if (value === null || value === undefined) return '';

  return String(value).trim();
};

/** Non-empty, trimmed values in the order given. */
const presentParts = (...values) => values.map(clean).filter(Boolean);

/**
 * One address as a single line, e.g. "Home: 12 Oak St, Phoenix, Arizona, 85001".
 * Missing pieces are skipped rather than leaving their separators behind.
 */
export const formatAddressLine = (address) => {
  if (!address || typeof address !== 'object') return '';

  const location = presentParts(
    address.address,
    address.address2,
    address.city,
    address.state,
    address.zip
  ).join(', ');

  if (!location) return '';

  const type = clean(address.add_type);

  return type ? `${type}: ${location}` : location;
};

/**
 * Every address on a contact, one per line. Returns '' when there is nothing
 * worth showing so callers can fall back to their own placeholder.
 */
export const formatAddressList = (addresses) => {
  if (!Array.isArray(addresses)) return '';

  return addresses.map(formatAddressLine).filter(Boolean).join('\n');
};

/**
 * A single field across every address on a contact, de-duplicated.
 * Backs the standalone City / State / Zip columns.
 */
export const formatAddressField = (addresses, field) => {
  if (!Array.isArray(addresses)) return '';

  const values = addresses.map((address) => clean(address?.[field])).filter(Boolean);

  return [...new Set(values)].join('\n');
};
